import { z } from 'zod';
import { encodeBody } from '../http/encode.js';
import { adjustmentSchema, balanceSchema, issueSchema, movementCreateSchema, movementSchema, rebuildResultSchema, receiptSchema, reversalSchema, singleBalanceSchema, } from '../schemas/stock.js';
const movementListSchema = z.array(movementSchema);
const balanceListSchema = z.array(balanceSchema);
/**
 * The ledger.
 *
 * There is no `update` and no `delete` on this class, and there never will be. Invariant 1
 * is enforced by the database — the application role holds only INSERT and SELECT on
 * `stock_movements` — so adding one here would produce a 403 from PostgreSQL, not a bug
 * report. `reverse` is the correction mechanism.
 */
export class StockApi {
    transport;
    constructor(transport) {
        this.transport = transport;
    }
    // ------------------------------------------------------------------- posting
    /** Stock in. The only shape that may carry a cost, and it is a per-unit rate. */
    receive(payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: '/v1/stock/receipts',
            body: encodeBody(receiptSchema, payload, 'receiveStock'),
            operation,
            schema: movementSchema,
            signal,
        });
    }
    /**
     * Stock out.
     *
     * Three distinct refusals a caller must handle separately:
     *   409 `InsufficientStockError`  — not enough on hand, and the warehouse forbids negative
     *   409 `ReservationRefusedError` — enough on hand, but hard-reserved elsewhere
     *   422 `ValidationError`         — e.g. a batch-tracked item with no batch named
     *
     * None of them is ever a silent partial post. An oversell is refused and nothing is
     * written; Accounts clamps the balance to zero and returns success.
     */
    issue(payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: '/v1/stock/issues',
            body: encodeBody(issueSchema, payload, 'issueStock'),
            operation,
            schema: movementSchema,
            signal,
        });
    }
    /** A signed delta. The server maps the sign to ADJUSTMENT_IN or ADJUSTMENT_OUT. */
    adjust(payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: '/v1/stock/adjustments',
            body: encodeBody(adjustmentSchema, payload, 'adjustStock'),
            operation,
            schema: movementSchema,
            signal,
        });
    }
    /** The canonical posting endpoint the three above are conveniences over. */
    post(payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: '/v1/movements',
            body: encodeBody(movementCreateSchema, payload, 'postMovement'),
            operation,
            schema: movementSchema,
            signal,
        });
    }
    /**
     * The only correction. A reason is required by the API and by a CHECK constraint.
     *
     * A movement can be reversed at most once (a UNIQUE on `reverses_movement_id`), and a
     * reversal cannot itself be reversed — so a skin should hide the control on a row that
     * already carries `reverses_movement_id`, and be ready for the 409 anyway.
     */
    reverse(movementId, payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: `/v1/movements/${encodeURIComponent(movementId)}/reverse`,
            body: encodeBody(reversalSchema, payload, 'reverseMovement'),
            operation,
            schema: movementSchema,
            signal,
        });
    }
    // ------------------------------------------------------------------ enquiry
    listMovements(filter = {}, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/movements',
            query: {
                item_id: filter.item_id,
                warehouse_id: filter.warehouse_id,
                stock_state: filter.stock_state,
                movement_type: filter.movement_type,
                reference_type: filter.reference_type,
                reference_id: filter.reference_id,
                transfer_id: filter.transfer_id,
                date_from: filter.date_from,
                date_to: filter.date_to,
                limit: filter.limit ?? 100,
                offset: filter.offset ?? 0,
            },
            schema: movementListSchema,
            signal,
        });
    }
    getMovement(movementId, signal) {
        return this.transport.request({
            method: 'GET',
            path: `/v1/movements/${encodeURIComponent(movementId)}`,
            schema: movementSchema,
            signal,
        });
    }
    listBalances(filter = {}, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/stock/balances',
            query: {
                item_id: filter.item_id,
                warehouse_id: filter.warehouse_id,
                stock_state: filter.stock_state,
                include_zero: filter.include_zero ?? false,
                limit: filter.limit ?? 200,
                offset: filter.offset ?? 0,
            },
            schema: balanceListSchema,
            signal,
        });
    }
    getBalance(params, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/stock/balance',
            query: {
                item_id: params.item_id,
                warehouse_id: params.warehouse_id,
                stock_state: params.stock_state ?? 'AVAILABLE',
            },
            schema: singleBalanceSchema,
            signal,
        });
    }
    /**
     * Audit the projection against the ledger.
     *
     * `apply: false` reports; `apply: true` repairs. A non-empty `discrepancies` is a defect
     * report — the projection is written in the same transaction as the movement, so it
     * should never drift — and a skin should present it as such rather than as routine
     * housekeeping.
     */
    rebuildBalances(apply, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: '/v1/stock/balances/rebuild',
            query: { apply },
            operation,
            schema: rebuildResultSchema,
            signal,
        });
    }
}
//# sourceMappingURL=stock.js.map